import axios from '../utils/axios';

class WizardService {
  /**
   * Initialize a new wizard session
   */
  async initializeWizard() {
    try {
      const response = await axios.post('/evaluations/wizard/start');
      return response.data;
    } catch (error) {
      return this.handleError(error);
    }
  }

  /**
   * Save wizard step data
   */
  async saveWizardStep(sessionId, step, data) {
    try {
      // Normalize data to match backend expectations
      const normalizedData = { ...data };

      // Specific transformations for different steps
      if (step === 'test_data') {
        // Ensure data is mapped correctly and set default values for missing fields
        normalizedData.data = normalizedData.data || [];

        // Preserve run_name if it exists in the input data
        if (!normalizedData.run_name && !data.run_name) {
          const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
          normalizedData.run_name = `Evaluation Run ${timestamp}`;
        } else if (data.run_name) {
          normalizedData.run_name = data.run_name;
        }

        normalizedData.provider = normalizedData.provider || 'openai';
        normalizedData.model = normalizedData.model || 'gpt-4';
        normalizedData.prompt = normalizedData.prompt || '';
        normalizedData.role = normalizedData.role || 'user';
      }

      const response = await axios.put(`/evaluations/wizard/${sessionId}/step/${step}`, normalizedData);
      return response.data;
    } catch (error) {
      // Return validation errors in a more structured way
      if (error.response?.status === 422 && error.response?.data?.errors) {
        return {
          success: false,
          error: error.response.data.message || 'Validation failed',
          errors: error.response.data.errors,
          status: 422
        };
      }
      return this.handleError(error);
    }
  }

  /**
   * Get wizard session data
   */
  async getWizardData(sessionId) {
    try {
      const response = await axios.get(`/evaluations/wizard/${sessionId}`);
      return response.data;
    } catch (error) {
      return this.handleError(error);
    }
  }

  /**
   * Complete wizard and create evaluation
   */
  async completeWizard(sessionId) {
    try {
      const response = await axios.post(`/evaluations/wizard/${sessionId}/complete`);
      return response.data;
    } catch (error) {
      return this.handleError(error);
    }
  }

  /**
   * Get evaluation templates (public + user's private templates)
   */
  async getEvaluationTemplates() {
    try {
      // Use the main templates endpoint which returns public + user's templates
      const response = await axios.get('/templates', {
        params: {
          per_page: 100 // Get more templates for selection
        }
      });
      return response.data;
    } catch (error) {
      return this.handleError(error);
    }
  }

  /**
   * Get evaluation examples by type
   */
  async getEvaluationExamples(type) {
    try {
      const response = await axios.get(`/evaluation-examples/${type}`);
      return response.data;
    } catch (error) {
      return this.handleError(error);
    }
  }

  /**
   * Execute evaluation with real-time tracking
   */
  async executeEvaluation(evaluationId) {
    try {
      const response = await axios.post(`/evaluations/${evaluationId}/execute`);
      return response.data;
    } catch (error) {
      return this.handleError(error);
    }
  }

  /**
   * Get execution progress
   */
  async getExecutionProgress(runId) {
    try {
      const response = await axios.get(`/evaluations/runs/${runId}/progress`);
      return response.data;
    } catch (error) {
      return this.handleError(error);
    }
  }

  /**
   * Get execution results
   */
  async getExecutionResults(runId) {
    try {
      const response = await axios.get(`/evaluations/runs/${runId}/results`);
      return response.data;
    } catch (error) {
      return this.handleError(error);
    }
  }

  /**
   * Cancel execution
   */
  async cancelExecution(runId) {
    try {
      const response = await axios.delete(`/evaluations/runs/${runId}`);
      return response.data;
    } catch (error) {
      return this.handleError(error);
    }
  }

  /**
   * Schedule recurring runs
   */
  async scheduleRecurringRuns(runId, scheduleConfig) {
    try {
      const response = await axios.post(`/evaluations/runs/${runId}/schedule`, scheduleConfig);
      return response.data;
    } catch (error) {
      return this.handleError(error);
    }
  }

  /**
   * Send results via email
   */
  async sendResultsEmail(runId, emailConfig) {
    try {
      const response = await axios.post(`/evaluations/runs/${runId}/send-email`, emailConfig);
      return response.data;
    } catch (error) {
      return this.handleError(error);
    }
  }

  /**
   * Send results via webhook
   */
  async sendResultsWebhook(runId, webhookConfig) {
    try {
      const response = await axios.post(`/evaluations/runs/${runId}/send-webhook`, webhookConfig);
      return response.data;
    } catch (error) {
      return this.handleError(error);
    }
  }

  /**
   * Track evaluation execution by run ID or name
   * @param {string|number} runIdOrName - The ID or name of the evaluation run
   * @returns {Promise} Execution tracking data
   */
  async trackEvaluationExecution(runIdOrName) {
    try {
      // If it's a number, use it as run ID directly
      if (typeof runIdOrName === 'number' || !isNaN(runIdOrName)) {
        const response = await axios.get(`/evaluations/runs/${runIdOrName}/progress`);
        return response.data;
      }

      // Otherwise, try to find the run by evaluation name
      // First get all evaluations to find the one with this name
      const evaluationsResponse = await axios.get('/evaluations');
      const evaluation = evaluationsResponse.data.data?.find(e => e.name === runIdOrName);

      if (!evaluation) {
        return {
          success: false,
          message: 'Evaluation not found',
          status: 404
        };
      }

      // Get the latest run for this evaluation
      const runsResponse = await axios.get(`/evaluations/${evaluation.id}/runs`);
      const latestRun = runsResponse.data.data?.[0];

      if (!latestRun) {
        return {
          success: false,
          message: 'No runs found for this evaluation',
          status: 404
        };
      }

      // Now get progress for the latest run
      const response = await axios.get(`/evaluations/runs/${latestRun.id}/progress`);
      return response.data;
    } catch (error) {
      return this.handleError(error);
    }
  }

  /**
   * Handle API errors consistently
   */
  handleError(error) {
    const message = error.response?.data?.message || error.message || 'An error occurred';
    const status = error.response?.status || 500;
    const errorCode = error.response?.data?.error_code || 'UNKNOWN_ERROR';

    console.error('Wizard API Error:', {
      message,
      status,
      errorCode,
      url: error.config?.url,
      method: error.config?.method,
      timestamp: new Date().toISOString(),
    });

    // Handle specific error cases
    if (status === 401) {
      // Token might be expired, redirect to login
      localStorage.removeItem('auth_token');
      window.location.href = '/login';
    }

    return {
      success: false,
      error: message,
      status,
      errorCode,
    };
  }
}

const wizardService = new WizardService();
export default wizardService;
